import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Result,
  Row,
  Space,
  Spin,
  Switch,
  Checkbox,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import {
  CheckCircleFilled,
  CreditCardOutlined,
  MinusCircleOutlined,
  SaveOutlined,
  ShopOutlined,
  TruckOutlined
} from '@ant-design/icons'
import ShopHint from '../../components/shop/ShopHint'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useMembership } from '../../hooks/useMembership'
import { usePermission } from '../../hooks/usePermission'
import { getApiErrorMessage } from '../../utils/api'
import {
  getShopSettings,
  setStoreEnabled,
  updateShopSettings
} from '../../api/shop'

const { Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SectionCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);
`

const CheckItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  .icon {
    font-size: 18px;
    margin-top: 2px;
    color: ${(p) => (p.$done ? '#52c41a' : '#d9d9d9')};
  }

  .body {
    flex: 1;
  }

  .label {
    font-weight: 500;
    color: ${(p) => (p.$done ? '#333' : '#666')};
  }

  .hint {
    font-size: 12px;
    color: #999;
    margin-top: 2px;
  }
`

/**
 * 付款／取貨方式卡片的標題區。
 * 標題與開關同一行、說明文字自成一行 —— 說明如果跟開關擠同一行，
 * 手機直向會卡字換行、開關被推到奇怪的位置。
 */
/**
 * 卡片標題列：自己排版取代 antd Card 的 title / extra。
 * antd 的 extra 在窄螢幕會跟長標題擠在同一行互相推擠，
 * 這裡改成可換行的 flex，狀態徽章擠不下時會自己掉到下一行。
 */
const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 16px;

  .head-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #3b3540;
  }
`

/** 販售準備狀態徽章：與 ShopHint 同一套色彩語言，取代 antd Tag */
const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  background: ${(p) => (p.$ok ? '#e9f6ec' : '#f9eed8')};
  color: ${(p) => (p.$ok ? '#3f7a4a' : '#8a6430')};
  border: 1px solid ${(p) => (p.$ok ? '#cfe8d4' : '#eddec0')};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => (p.$ok ? '#5aa668' : '#c98a3d')};
  }
`

const MethodHeader = styled.div`
  margin-bottom: ${(p) => (p.$expanded ? '16px' : '0')};

  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .desc {
    font-size: 12px;
    color: #999;
    line-height: 1.7;
    margin-top: 4px;
  }
`

export default function ShopSettingsPage () {
  const navigate = useNavigate()
  const { hasFeature } = useMembership()
  const { can } = usePermission()
  const [form] = Form.useForm()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [enabling, setEnabling] = useState(false)
  const [readiness, setReadiness] = useState(null)
  const [gateway, setGateway] = useState(null)
  const [feeSettings, setFeeSettings] = useState(null)

  // 開關要即時控制子欄位的展開收合，所以獨立於 form 追蹤一份
  const [toggles, setToggles] = useState({
    onlineOrderEnabled: false,
    online: false,
    bankTransfer: false,
    cod: false,
    homeDelivery: false,
    storePickup: false,
    cvsPickup: false
  })

  const applySettings = useCallback(
    (settings) => {
      form.setFieldsValue(settings)
      setToggles({
        onlineOrderEnabled: settings.onlineOrderEnabled,
        online: settings.payment.online.enabled,
        bankTransfer: settings.payment.bankTransfer.enabled,
        cod: settings.payment.cod.enabled,
        homeDelivery: settings.delivery.homeDelivery.enabled,
        storePickup: settings.delivery.storePickup.enabled,
        cvsPickup: settings.delivery.cvsPickup?.enabled
      })
    },
    [form]
  )

  const fetchSettings = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getShopSettings()
      const { settings, readiness: rd, paymentFeeSettings } = res.data.data
      applySettings(settings)
      setReadiness(rd)
      setGateway(rd?.gateway || null)
      setFeeSettings(paymentFeeSettings)
    } catch (e) {
      message.error(getApiErrorMessage(e, '載入商店設定失敗'))
    } finally {
      setLoading(false)
    }
  }, [applySettings])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  /** 啟用商店功能（Business.storeEnabled + Store.isEnabled） */
  const handleEnableStore = async (next) => {
    setEnabling(true)
    try {
      await setStoreEnabled(next)
      message.success(next ? '商店功能已啟用' : '商店功能已停用')
      await fetchSettings()
    } catch (e) {
      message.error(getApiErrorMessage(e, '切換商店功能失敗'))
    } finally {
      setEnabling(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const values = await form.validateFields()
      const res = await updateShopSettings(values)
      const { settings, readiness: rd } = res.data.data
      applySettings(settings)
      setReadiness(rd)
      setGateway(rd?.gateway || null)
      message.success('設定已儲存')
    } catch (e) {
      if (e?.errorFields) return
      // 後端會把所有問題放在 data.errors。只顯示第一條的話，
      // 店家會改一個存一次、被擋五次才知道全部要改什麼。
      const errors = e?.response?.data?.data?.errors
      if (Array.isArray(errors) && errors.length > 1) {
        message.error({
          content: (
            <div style={{ textAlign: 'left' }}>
              {errors.map((msg, i) => (
                <div key={i}>• {msg}</div>
              ))}
            </div>
          ),
          duration: 6
        })
      } else {
        message.error(getApiErrorMessage(e, '儲存失敗'))
      }
    } finally {
      setSaving(false)
    }
  }

  if (!hasFeature('shopOrder')) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通線上商店'
          subTitle='請升級至高階方案以使用線上商店功能。'
        />
      </PageWrapper>
    )
  }

  if (!can('shopSetting:manage')) {
    return (
      <PageWrapper>
        <Result
          status='403'
          title='沒有權限'
          subTitle='只有店長以上的角色可以調整商店設定。若需處理訂單，請前往「商店訂單」。'
          extra={
            <Button type='primary' onClick={() => navigate('/shop/orders')}>
              前往商店訂單
            </Button>
          }
        />
      </PageWrapper>
    )
  }

  if (loading) {
    return (
      <PageWrapper>
        <div
          style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}
        >
          <Spin size='large' />
        </div>
      </PageWrapper>
    )
  }

  const canEnableOnlineCard = gateway?.approved

  return (
    <PageWrapper>
      <ContentWrapper>
        <SectionCard>
          <CardHead>
            <div className='head-title'>
              <ShopOutlined />
              開始販售前的準備
            </div>
            {/* 綠燈只代表「現在真的在賣」。
                只看 readiness.ready 的話，總開關關著也會顯示綠色的
                「已可開始販售」，店家會以為已經在接單了 */}
            <StatusChip
              $ok={!!readiness?.ready && !!toggles.onlineOrderEnabled}
            >
              {!readiness?.ready
                ? '尚未完成設定'
                : toggles.onlineOrderEnabled
                  ? '販售中'
                  : '準備就緒，尚未開賣'}
            </StatusChip>
          </CardHead>

          {readiness?.checks?.map((c) => (
            <CheckItem key={c.key} $done={c.done}>
              {c.done
                ? <CheckCircleFilled className='icon' />
                : <MinusCircleOutlined className='icon' />}
              <div className='body'>
                <div className='label'>
                  {c.label}
                  {c.key === 'products' && c.count > 0 && (
                    <Text
                      type='secondary'
                      style={{ marginLeft: 8, fontSize: 12 }}
                    >
                      已上架 {c.count} 項
                    </Text>
                  )}
                </div>
                {!c.done && <div className='hint'>{c.hint}</div>}
              </div>
              {c.key === 'products' && !c.done && (
                <Button
                  size='small'
                  onClick={() => navigate('/shop/products')}
                >
                  前往上架
                </Button>
              )}
              {c.key === 'storeEnabled' && !c.done && (
                <Button
                  size='small'
                  type='primary'
                  loading={enabling}
                  onClick={() => handleEnableStore(true)}
                >
                  立即啟用
                </Button>
              )}
            </CheckItem>
          ))}

          <Divider style={{ margin: '16px 0' }} />

          <Form form={form} layout='vertical'>
            <Row align='middle' gutter={16}>
              <Col flex='auto'>
                <Text strong style={{ fontSize: 16 }}>
                  線上商店總開關
                </Text>
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                  關閉時客人仍可瀏覽商品，但無法加入購物車或下單
                </div>
              </Col>
              <Col>
                <Form.Item
                  name='onlineOrderEnabled'
                  valuePropName='checked'
                  noStyle
                >
                  <Switch
                    checkedChildren='營業中'
                    unCheckedChildren='已關閉'
                    onChange={(v) =>
                      setToggles((t) => ({ ...t, onlineOrderEnabled: v }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            {readiness?.ready && !toggles.onlineOrderEnabled && (
              <ShopHint
                tone='warning'
                title='前置準備都完成了，還差最後一步'
                style={{ margin: '4px 0 16px' }}
              >
                打開上方的「線上商店總開關」才會開始接單。目前客人可以瀏覽商品，
                但無法加入購物車或下單。
              </ShopHint>
            )}

            {!toggles.onlineOrderEnabled && (
              <Form.Item
                name='pauseNotice'
                label='暫停接單公告'
                extra='會顯示在商品頁與購物車，例如「春節期間 2/8-2/14 暫停出貨」'
                style={{ marginTop: 16 }}
              >
                <Input.TextArea
                  rows={2}
                  maxLength={100}
                  showCount
                  placeholder='選填'
                />
              </Form.Item>
            )}

            <Divider orientation='left'>
              <Space>
                <CreditCardOutlined />
                付款方式
              </Space>
            </Divider>

            {readiness?.onlinePaymentBroken && (
              <ShopHint
                tone='error'
                title='線上刷卡目前無法使用'
                style={{ marginBottom: 16 }}
              >
                原因：{gateway?.reason}
                。客人在結帳頁選擇刷卡會付款失敗，建議先關閉此選項或盡快完成金流設定。
              </ShopHint>
            )}

            <Card size='small' style={{ marginBottom: 12 }}>
              <MethodHeader $expanded={toggles.online}>
                <div className='title-row'>
                  <Text strong>線上刷卡</Text>
                  <Tooltip
                    title={
                      canEnableOnlineCard
                        ? ''
                        : `${gateway?.reason}。需綁定您專屬的藍新金流帳號並通過審核後才能啟用。`
                    }
                  >
                    <Form.Item
                      name={['payment', 'online', 'enabled']}
                      valuePropName='checked'
                      noStyle
                    >
                      <Switch
                        disabled={!canEnableOnlineCard}
                        onChange={(v) => setToggles((t) => ({ ...t, online: v }))}
                      />
                    </Form.Item>
                  </Tooltip>
                </div>
                <div className='desc'>
                  客人下單後立即以信用卡付款，款項直接進入
                  <Text strong>您自己的金流帳戶</Text>
                </div>
              </MethodHeader>

              {!canEnableOnlineCard && (
                <ShopHint
                  tone='info'
                  title={gateway?.reason || '尚未綁定金流帳號'}
                  style={{ marginTop: 12 }}
                  action={
                    <Button
                      size='small'
                      type='primary'
                      ghost
                      onClick={() => navigate('/settings/payment')}
                    >
                      前往金流設定
                    </Button>
                  }
                >
                  線上刷卡的貨款會直接進入您自己的收單帳戶，因此必須先完成金流綁定與審核。在此之前，您仍可使用轉帳匯款或貨到付款收款。
                </ShopHint>
              )}

              {toggles.online && canEnableOnlineCard && feeSettings && (
                <ShopHint
                  tone='info'
                  title={`刷卡手續費率 ${feeSettings.cardFeeRate}%`}
                  style={{ marginTop: 12 }}
                >
                  手續費會自動計入訂單成本，在營運報表中列為「金流服務費」。
                </ShopHint>
              )}
            </Card>

            <Card size='small' style={{ marginBottom: 12 }}>
              <MethodHeader $expanded={toggles.bankTransfer}>
                <div className='title-row'>
                  <Text strong>轉帳匯款</Text>
                  <Form.Item
                    name={['payment', 'bankTransfer', 'enabled']}
                    valuePropName='checked'
                    noStyle
                  >
                    <Switch
                      onChange={(v) =>
                        setToggles((t) => ({ ...t, bankTransfer: v }))}
                    />
                  </Form.Item>
                </div>
                <div className='desc'>客人匯款後回報末五碼，您確認入帳再出貨</div>
              </MethodHeader>

              {toggles.bankTransfer && (
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['payment', 'bankTransfer', 'bankName']}
                      label='銀行名稱'
                      rules={[{ required: true, message: '請填寫銀行名稱' }]}
                    >
                      <Input placeholder='例：國泰世華銀行' />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['payment', 'bankTransfer', 'bankCode']}
                      label='銀行代碼'
                    >
                      <Input placeholder='例：013' />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['payment', 'bankTransfer', 'accountNo']}
                      label='匯款帳號'
                      rules={[{ required: true, message: '請填寫匯款帳號' }]}
                    >
                      <Input placeholder='客人會看到這個帳號' />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['payment', 'bankTransfer', 'accountName']}
                      label='戶名'
                      rules={[{ required: true, message: '請填寫戶名' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['payment', 'bankTransfer', 'deadlineHours']}
                      label='匯款期限'
                      extra='逾期未回報的訂單會自動取消並回補庫存'
                    >
                      <InputNumber
                        min={1}
                        max={720}
                        addonAfter='小時'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name={['payment', 'bankTransfer', 'instruction']}
                      label='匯款說明'
                      extra='顯示在結帳頁與訂單詳情，例如提醒客人備註姓名'
                    >
                      <Input.TextArea rows={2} maxLength={200} showCount />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>

            <Card size='small' style={{ marginBottom: 12 }}>
              <MethodHeader $expanded={toggles.cod}>
                <div className='title-row'>
                  <Text strong>貨到付款</Text>
                  <Form.Item
                    name={['payment', 'cod', 'enabled']}
                    valuePropName='checked'
                    noStyle
                  >
                    <Switch
                      onChange={(v) => setToggles((t) => ({ ...t, cod: v }))}
                    />
                  </Form.Item>
                </div>
                <div className='desc'>
                  訂單成立後直接進入待出貨，收到貨款時再標記為已付款
                </div>
              </MethodHeader>

              {toggles.cod && (
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['payment', 'cod', 'extraFee']}
                      label='貨到付款手續費'
                      extra='會加在訂單總額上'
                    >
                      <InputNumber
                        min={0}
                        addonBefore='NT$'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['payment', 'cod', 'maxAmount']}
                      label='金額上限'
                      extra='超過此金額不開放貨到付款，降低棄單風險'
                    >
                      <InputNumber
                        min={0}
                        addonBefore='NT$'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>

            <Divider orientation='left'>
              <Space>
                <TruckOutlined />
                取貨方式
              </Space>
            </Divider>

            <ShopHint
              tone='info'
              title='物流由您自行安排'
              style={{ marginBottom: 12 }}
            >
              系統不串接物流商，出貨時請自行填寫物流商與單號，客人會收到通知。
            </ShopHint>

            <Card size='small' style={{ marginBottom: 12 }}>
              <MethodHeader $expanded={toggles.homeDelivery}>
                <div className='title-row'>
                  <Text strong>宅配到府</Text>
                  <Form.Item
                    name={['delivery', 'homeDelivery', 'enabled']}
                    valuePropName='checked'
                    noStyle
                  >
                    <Switch
                      onChange={(v) =>
                        setToggles((t) => ({ ...t, homeDelivery: v }))}
                    />
                  </Form.Item>
                </div>
              </MethodHeader>

              {toggles.homeDelivery && (
                <Row gutter={12}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={['delivery', 'homeDelivery', 'fee']}
                      label='運費'
                    >
                      <InputNumber
                        min={0}
                        addonBefore='NT$'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={['delivery', 'homeDelivery', 'freeThreshold']}
                      label='免運門檻'
                      extra='填 0 表示不提供免運'
                    >
                      <InputNumber
                        min={0}
                        addonBefore='NT$'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={['delivery', 'homeDelivery', 'remoteAreaFee']}
                      label='離島加價'
                      extra='澎湖、金門、馬祖'
                    >
                      <InputNumber
                        min={0}
                        addonBefore='NT$'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name={['delivery', 'homeDelivery', 'note']}
                      label='出貨時效說明'
                      extra='例：訂單成立後 3 個工作天內出貨'
                    >
                      <Input maxLength={100} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>

            <Card size='small' style={{ marginBottom: 12 }}>
              <MethodHeader $expanded={toggles.storePickup}>
                <div className='title-row'>
                  <Text strong>到店取貨</Text>
                  <Form.Item
                    name={['delivery', 'storePickup', 'enabled']}
                    valuePropName='checked'
                    noStyle
                  >
                    <Switch
                      onChange={(v) =>
                        setToggles((t) => ({ ...t, storePickup: v }))}
                    />
                  </Form.Item>
                </div>
              </MethodHeader>

              {toggles.storePickup && (
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['delivery', 'storePickup', 'fee']}
                      label='手續費'
                    >
                      <InputNumber
                        min={0}
                        addonBefore='NT$'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={['delivery', 'storePickup', 'businessHours']}
                      label='取貨時段'
                    >
                      <Input placeholder='例：週二至週日 11:00-20:00' />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name={['delivery', 'storePickup', 'address']}
                      label='取貨地址'
                      rules={[{ required: true, message: '請填寫取貨地址' }]}
                      extra='客人會收到這個地址，留空則使用商家地址'
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>

            <Card size='small' style={{ marginBottom: 12 }}>
              <MethodHeader $expanded={toggles.cvsPickup}>
                <div className='title-row'>
                  <Text strong>超商取貨</Text>
                  <Form.Item
                    name={['delivery', 'cvsPickup', 'enabled']}
                    valuePropName='checked'
                    noStyle
                  >
                    <Switch
                      onChange={(v) =>
                        setToggles((t) => ({ ...t, cvsPickup: v }))}
                    />
                  </Form.Item>
                </div>
              </MethodHeader>

              {toggles.cvsPickup && (
                <>
                  <ShopHint
                    tone='info'
                    title='客人會自行填寫取貨門市'
                    style={{ marginBottom: 12 }}
                  >
                    目前尚未串接超商的門市查詢，客人在結帳時自己填寫超商品牌、門市名稱與地址，你收到訂單後自行到超商寄件，再回填寄件編號。
                  </ShopHint>
                  <Row gutter={12}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name={['delivery', 'cvsPickup', 'fee']}
                        label='運費'
                        extra='超商取貨通常比宅配便宜，這裡與宅配分開設定'
                      >
                        <InputNumber
                          min={0}
                          addonBefore='NT$'
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name={['delivery', 'cvsPickup', 'freeThreshold']}
                        label='免運門檻'
                        extra='填 0 表示不提供免運'
                      >
                        <InputNumber
                          min={0}
                          addonBefore='NT$'
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        name={['delivery', 'cvsPickup', 'brands']}
                        label='開放的超商'
                        rules={[
                          {
                            required: true,
                            message: '請至少選擇一家超商'
                          }
                        ]}
                      >
                        <Checkbox.Group
                          options={[
                            { label: '7-ELEVEN', value: 'seven' },
                            { label: '全家', value: 'family' },
                            { label: '萊爾富', value: 'hilife' },
                            { label: 'OK mart', value: 'okmart' }
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        name={['delivery', 'cvsPickup', 'allowCod']}
                        label='開放超商取貨付款'
                        valuePropName='checked'
                        extra='關閉時客人必須先付款（線上刷卡或轉帳）才能選超商取貨。取貨付款的風險是客人不去領，包裹會退回並產生來回運費。'
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        name={['delivery', 'cvsPickup', 'note']}
                        label='寄件時效說明'
                      >
                        <Input placeholder='例：出貨後約 2-3 個工作天到店，到店會收到超商簡訊' />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </Card>

            <Divider orientation='left'>營運規則</Divider>

            <Row gutter={12}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['orderRules', 'minOrderAmount']}
                  label='最低消費金額'
                  extra='填 0 表示不限'
                >
                  <InputNumber
                    min={0}
                    addonBefore='NT$'
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['orderRules', 'autoCancelUnpaidHours']}
                  label='未付款自動取消'
                  extra='逾時後自動取消並回補庫存'
                >
                  <InputNumber
                    min={1}
                    addonAfter='小時'
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['orderRules', 'autoCompleteDays']}
                  label='出貨後自動完成'
                  extra='填 0 表示不自動完成'
                >
                  <InputNumber
                    min={0}
                    addonAfter='天'
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['orderRules', 'outOfStockPolicy']}
                  label='商品售完時'
                >
                  <Radio.Group>
                    <Radio value='show_soldout'>仍顯示，標示為「已售完」</Radio>
                    <Radio value='hide'>直接隱藏</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['orderRules', 'returnPolicy']}
                  label={
                    <Space>
                      退換貨政策
                      <Tag color='red'>必填</Tag>
                    </Space>
                  }
                  extra='網路販售依《消費者保護法》有七天猶豫期，此說明會顯示在商品頁與結帳頁'
                  rules={[{ required: true, message: '請填寫退換貨政策' }]}
                >
                  <Input.TextArea rows={4} maxLength={1000} showCount />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['orderRules', 'checkoutNotice']}
                  label='結帳頁提醒'
                  extra='選填，例如提醒客人確認收件資訊'
                >
                  <Input.TextArea rows={2} maxLength={200} showCount />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </SectionCard>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button onClick={fetchSettings} disabled={saving}>
            重新載入
          </Button>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            儲存設定
          </Button>
        </div>
      </ContentWrapper>
    </PageWrapper>
  )
}
